// MACHINERY V2 (Stroje) — fleet card kebab menu (M2.2).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.2 + §10 (permissions).
// Reference impl: design_handoff_machinery_v3/machinery-live.jsx (QuickMenu,
// QUICK_MENU_ITEMS) — rebuilt on Radix DropdownMenu + the `.ms-menu` atom
// instead of the handoff's hand-rolled absolute-positioned popup.
//
// ACTION SET = THE DATA PACKAGE, NOT THE ARTBOARD. The artboard draws an
// abbreviated menu (Profil / QR / Vytvoriť SS / Vyradiť); the data package §9
// "Actions" row lists the full live set, and the golden rule is that the data
// package wins where the artboard omits. So Create RFS / Edit / Assign group /
// Duplicate / Restore are all here, each behind the same gate the live grid
// applies.
//
// PERMISSIONS (§10) — one gate per row, every one of them living in
// fleetActions.ts (this component only paints the list it is handed):
//   Read  : Profil, Vytvoriť servisnú správu  (Workers included)
//   Write : QR kód, Vytvoriť servisnú žiadosť, Upraviť detaily, Duplikovať,
//           Priradiť do skupiny, Vyradiť, Obnoviť
//   Admin : Zmazať natrvalo — hard delete, decommissioned machines only
//           (SHARED_DEPLOY_AND_RULES §6 "Hard delete endpoints = Admin").
//   THIS IS NOT BLANKET LIVE PARITY. Two rows differ in kind:
//   · QR IS Machinery:WRITE (SEC-REQ-B7-12) on EVERY branch, decommissioned
//     included — its dialog reads a Write-level nameplate GET, so a Worker
//     would get a 403 and an empty dialog. Live AGREES: all four live surfaces
//     wrap it in PermissionGuard(Machinery, Write), so this row is straight
//     conformance, not a new gate. Do not ungate; the four guard lines are
//     cited in fleetActions.ts.
//   · OBNOVIŤ is Write in v2 but UNGATED on all four live surfaces. That gap
//     is a DELIBERATE, approved divergence and v2 is the correct side: the
//     restore endpoint is Write-only server-side
//     (CustomerMachinesController.cs:364-365), so live's ungated button 403s
//     for every Worker who clicks it. Live's is the logged defect — do NOT
//     ungate to "match live". Full citation in fleetActions.ts.
//   Profil is the one Read row that survives on a decommissioned machine —
//   unconditional there, matching the live cards.
//
// THIS COMPONENT HAS TWO CONSUMERS AND THEY COLLAPSE DIFFERENTLY. Do not
// describe one and assume the other:
//   · FLEET (FleetCard / FleetRow) — a decommissioned machine gives
//     Profil · QR · Obnoviť · Zmazať natrvalo to an Admin, the same minus the
//     delete row to a Write user, and Profil alone to a Worker. The list is
//     never empty, so the kebab always renders.
//   · PROFILE (profile/ProfileHero via useProfileActions.ts) — the same state
//     collapses HARDER, because PROMOTED_OUT_OF_KEBAB filters `profile`
//     (self-referential on the profile), `restore` AND `delete` (both promoted
//     into ProfileDecommissionBanner), while `reportProblem` is suppressed on a
//     decommissioned machine. An Admin and a Write user are therefore BOTH left
//     with QR ONLY, and a Worker with an EMPTY list — which this component renders as no kebab
//     at all (the `actions.length === 0` early return below). That empty case
//     is the profile's alone; ProfileHero additionally guards its whole action
//     column so nothing renders a blank 190px gutter.
//
// PORTAL RULE (MachineryV2Layout.tsx:20-39): DropdownMenu.Content portals to
// document.body, outside `.machinery-v2-root`, where the scoped vars do not
// resolve. Content re-carries the scope class, sets inline
// background:"transparent", paints on the inner `.ms-menu` div, and reads
// `palette` live on EVERY render. Same countermeasure as
// chrome/ChromeRightMenus.tsx `MenuContent` (canonical).
//
// STOP PROPAGATION — MOUSE **AND** KEYBOARD, AND IT LIVES HERE (M2.6, C4).
// This menu sits inside surfaces whose own onClick/onKeyDown navigate to the
// machine or the report. Radix portals the DOM to <body>, but React synthetic
// events still bubble the COMPONENT tree into the opener, so:
//   · MOUSE    — the trigger stops click propagation (:141 below) and the
//                portalled content does the same (:170), or picking
//                "Duplikovať" would also navigate away mid-action.
//   · KEYBOARD — BOTH ends need it, and for two DIFFERENT reasons:
//                OPENING — `DropdownMenu.Trigger` calls preventDefault() on
//                Enter/Space but does NOT stop propagation, so a keyboard user
//                who tabbed to the kebab and pressed Enter opened the menu AND
//                fired the parent's onKeyDown underneath it. The trigger stops
//                those two keys (:143-145).
//                SELECTING — Radix's `MenuItem` keydown does
//                `currentTarget.click(); preventDefault()` and likewise never
//                stops propagation, so arrowing to a row and pressing Enter
//                fired the action AND the host's onKeyDown. Content stops the
//                same two keys (:187-189). The trigger guard alone did NOT
//                cover this: it never sees a keydown aimed at an open menu.
// STANDING RULE: popup / tooltip / menu content inside a clickable surface must
// stopPropagation on the MOUSE **AND** the KEYBOARD activation paths. One
// without the other is not a guard.
// The guard is CENTRAL here rather than per call site, so fleet cards, fleet
// rows, the profile hero and the SR list all inherit it. The two call-site
// wrappers M2.5 added (profile/tabs/srRows.tsx:124-131 and :185-193 — the
// precedent this generalises, personally re-read at M2.6) are LEFT IN PLACE:
// belt-and-braces on a destructive-navigation path, and removing shipped guards
// buys nothing.
// Only the two ACTIVATION keys are stopped, on either end. Radix's own keyboard
// navigation inside an OPEN menu (arrows, Escape, Home/End, typeahead) is
// untouched: those keys are never in the `Enter | " "` test, and an ancestor
// seeing them is harmless. Neither guard calls preventDefault, which is what
// keeps Radix's own handlers running — it composes props.onKeyDown BEFORE its
// own and skips its own on a defaultPrevented event.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { MbKebabGlyph } from "../mobile/mobileGlyphs";

const KebabGlyph: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden>
    <circle cx="7" cy="2.5" r="1.2" />
    <circle cx="7" cy="7" r="1.2" />
    <circle cx="7" cy="11.5" r="1.2" />
  </svg>
);

export interface FleetMenuAction {
  id: string;
  labelKey: string;
  onSelect: () => void;
  /** Renders in --red and sits below a separator. */
  danger?: boolean;
  /** Radix suppresses onSelect entirely; the row also dims. Used for an action
   *  whose flow is already running (MDEL-FE review finding 2) — the menu closes
   *  on select, so this is what a user sees if they re-open it mid-flight. */
  disabled?: boolean;
}

export interface FleetCardMenuProps {
  actions: FleetMenuAction[];
  ariaLabel: string;
  /**
   * >>> 44x44 TRIGGER, FOR THE BELOW-1280 SURFACES ONLY (M2.16b). <<<
   *
   * The painted 28px box below is a DESKTOP POINTER size and stays the default,
   * so every >= 1280 consumer (FleetCard, FleetRow, the V3 table row) renders
   * byte-identically to before this prop existed. The law amended at M2.16a's
   * fix pass — ">= 44px BOTH axes below 1280, minWidth accompanies height" —
   * makes 28 illegal on the compact V1 card, which is the first phone/tablet
   * surface to carry this menu.
   *
   * It is a SIZE, not a second component: the click AND keyboard propagation
   * guards, the portal scope class, the palette and the `.mi` rows are the
   * asset here, and forking a "mobile kebab" would fork all four. The glyph
   * swaps to mobileGlyphs.MbKebabGlyph, which M2.16a drew at 16px explicitly
   * "sized for a 44px touch target" and which FleetMobileHeader's own kebab
   * already uses — one mobile kebab drawing, not two.
   */
  touch?: boolean;
}

const FleetCardMenu: React.FC<FleetCardMenuProps> = ({
  actions,
  ariaLabel,
  touch = false,
}) => {
  const { translate } = useTranslation("myMachinery");
  const palette = useAppSelector((s) => s.machineryV2Ui.palette);

  if (actions.length === 0) return null;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          // The card navigates on click; opening its own menu must not.
          onClick={(e) => e.stopPropagation()}
          // ...and neither must opening it from the keyboard. See the header.
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") e.stopPropagation();
          }}
          style={{
            // Both axes move together — see the `touch` prop's note. The
            // radius steps with the box so a 44px square does not read as a
            // near-circle at 6px.
            width: touch ? 44 : 28,
            height: touch ? 44 : 28,
            // undefined, NOT 28, on the desktop path: emitting a declaration the
            // >= 1280 button never had would be a DOM change on a surface this
            // sub-phase must leave byte-identical. The 44 is the law below 1280.
            minWidth: touch ? 44 : undefined,
            padding: 0,
            border: "1px solid var(--border)",
            background: "var(--card)",
            color: "var(--ink-2)",
            cursor: "pointer",
            borderRadius: touch ? 10 : 6,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          {touch ? <MbKebabGlyph /> : <KebabGlyph />}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={`machinery-v2-root p-${palette} mod-stroje`}
          align="end"
          sideOffset={6}
          // Same reason as the trigger: a click that lands on the menu surface
          // (a gap, a separator) must not fall through to the card.
          onClick={(e) => e.stopPropagation()}
          // ...and the KEYBOARD activation path too, mirroring the trigger's
          // guard above. Radix's MenuItem keydown does
          // `currentTarget.click(); preventDefault()` for Enter/Space but never
          // stopPropagation (@radix-ui/react-menu/dist/index.mjs:401-408 +
          // SELECTION_KEYS :26, personally read), so arrowing to "Duplikovať"
          // and pressing Enter fired the action AND the host row's onKeyDown
          // underneath it, tearing the user to the profile mid-action. Fixed
          // HERE rather than per call site, so every consumer inherits it: V1
          // cards, V2 rows, the V3 table and the V4 surfaces still to come.
          // stopPropagation ONLY — NEVER preventDefault. Radix runs
          // props.onKeyDown BEFORE its own and skips its own when the event is
          // defaultPrevented (@radix-ui/primitive/dist/index.mjs:3-10), so
          // preventing here would suppress Radix's own Content handling.
          // Arrows / Escape / Home / End / typeahead are deliberately NOT
          // stopped — Radix's Content owns those and an ancestor seeing them
          // is harmless.
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") e.stopPropagation();
          }}
          style={{ background: "transparent", zIndex: 1290 }}
        >
          {/* role="none": Radix puts role="menu" on Content and role="menuitem"
              on each Item; this presentational box sits between them and must
              not break the ARIA parent-child relationship. */}
          <div className="ms-menu" role="none" style={{ width: 214 }}>
            {actions.map((a, i) => (
              <React.Fragment key={a.id}>
                {a.danger && i > 0 && (
                  <div
                    style={{
                      height: 1,
                      background: "var(--border-soft)",
                      margin: "5px 4px",
                    }}
                  />
                )}
                <DropdownMenu.Item
                  className="mi"
                  onSelect={a.onSelect}
                  disabled={a.disabled}
                  style={{
                    ...(a.danger ? { color: "var(--red)" } : null),
                    ...(a.disabled ? { opacity: 0.45, cursor: "default" } : null),
                  }}
                >
                  <span>{translate(a.labelKey)}</span>
                </DropdownMenu.Item>
              </React.Fragment>
            ))}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default FleetCardMenu;
